import React, { useState, useEffect } from "react";
import { Card, Row, Col, CardHeader, CardBody, Button } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar, faEdit } from "@fortawesome/free-solid-svg-icons";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import moment from "moment";
import { useParams } from "react-router-dom";
import SchedulingModal from "./SchedulingModal";
import { useScheduling } from "../../../../providers/schedulingProvider";
import { schedulingApi } from "../../../../services/schedulingServices";
import Loader from "../../../Loader";
import ConfirmationModal from "../../../ConfirmationModal";
import InformationModal from "../../../InformationModal";

const Scheduling = () => {
  const { id: jobId } = useParams();
  const defaultMonth = new Date();
  const [month, setMonth] = useState(defaultMonth);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isInformationModalOpen, setIsInformationModalOpen] = useState(false);
  const [informationModalData, setInformationModalData] = useState(null);
  const [datesToSave, setDatesToSave] = useState(null);
  const [schedulingData, setSchedulingData] = useScheduling();
  const { dates, loading } = schedulingData;

  useEffect(() => {
    const fetchScheduling = async () => {
      try {
        const data = await schedulingApi.getSchedulingByJobId(jobId);
        setSchedulingData({ dates: data, loading: false, error: null });
      } catch (error) {
        setSchedulingData({ dates: null, loading: false, error });
      }
    };
    fetchScheduling();
  }, [jobId, setSchedulingData]);

  const legendItems = [
    { color: "#4984d3", label: "Sequence Ship" },
    { color: "#dc6af7", label: "Transmittal Need" },
    { color: "#5f7f5c", label: "Processed / Kitted Need" },
    { color: "#c79442", label: "Fab Complete" },
    { color: "#b53c36", label: "Back from OP" },
  ];

  const highlightedDates = dates
    ? {
        sequenceShip: dates.sequenceShipDate
          ? [new Date(dates.sequenceShipDate)]
          : [],
        processedKittedNeed: dates.processedKittedNeedDate
          ? [new Date(dates.processedKittedNeedDate)]
          : [],
        backFromOP: dates.backFromOpDate
          ? [new Date(dates.backFromOpDate)]
          : [],
        transmittalNeed: dates.transmittalNeedDate
          ? [new Date(dates.transmittalNeedDate)]
          : [],
        fabComplete: dates.fabCompleteDate
          ? [new Date(dates.fabCompleteDate)]
          : [],
      }
    : {};

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const handleSaveDates = (newDates) => {
    setDatesToSave(newDates);
    setIsConfirmationModalOpen(true);
  };

  const doSave = async () => {
    toggleModal();
    try {
      let result;
      if (dates) {
        result = await schedulingApi.updateScheduling(jobId, datesToSave);
      } else {
        result = await schedulingApi.createScheduling(jobId, datesToSave);
      }
      setSchedulingData({ dates: result, loading: false, error: null });
      setInformationModalData({
        title: "Success",
        body: "Scheduling dates saved successfully.",
      });
    } catch (error) {
      setInformationModalData({
        title: "Error",
        body: "An error occurred while saving the scheduling dates.",
      });
    } finally {
      setIsConfirmationModalOpen(false);
      setIsInformationModalOpen(true);
    }
  };

  const modifiers = {
    sequenceShip: highlightedDates.sequenceShip,
    processedKittedNeed: highlightedDates.processedKittedNeed,
    backFromOP: highlightedDates.backFromOP,
    transmittalNeed: highlightedDates.transmittalNeed,
    fabComplete: highlightedDates.fabComplete,
  };

  const modifiersStyles = {
    sequenceShip: {
      borderRadius: "50%",
      borderColor: "#4984d3",
      borderWidth: "2px",
      backgroundColor: "rgba(73, 132, 211, 0.2)",
      borderStyle: "solid",
    },
    processedKittedNeed: {
      borderRadius: "50%",
      borderColor: "#5f7f5c",
      borderWidth: "2px",
      backgroundColor: "rgba(95, 127, 92, 0.2)",
      borderStyle: "solid",
    },
    backFromOP: {
      borderRadius: "50%",
      borderColor: "#b53c36",
      borderWidth: "2px",
      backgroundColor: "rgba(181, 60, 54, 0.2)",
      borderStyle: "solid",
    },
    transmittalNeed: {
      borderRadius: "50%",
      borderColor: "#dc6af7",
      borderWidth: "2px",
      backgroundColor: "rgba(220, 106, 247, 0.2)",
      borderStyle: "solid",
    },
    fabComplete: {
      borderRadius: "50%",
      borderColor: "#c79442",
      borderWidth: "2px",
      backgroundColor: "rgba(199, 148, 66, 0.2)",
      borderStyle: "solid",
    },
    today: {
      fontWeight: "bold",
      color: "#4984d3",
    },
  };

  const modalDates = dates
    ? {
        sequenceShipDate: dates.sequenceShipDate
          ? new Date(dates.sequenceShipDate)
          : null,
        processedKittedNeedDate: dates.processedKittedNeedDate
          ? new Date(dates.processedKittedNeedDate)
          : null,
        backFromOpDate: dates.backFromOpDate
          ? new Date(dates.backFromOpDate)
          : null,
        transmittalNeedDate: dates.transmittalNeedDate
          ? new Date(dates.transmittalNeedDate)
          : null,
        fabCompleteDate: dates.fabCompleteDate
          ? new Date(dates.fabCompleteDate)
          : null,
      }
    : {};

  if (loading) {
    return (
      <Card className="m-0 p-3 h-100">
        <Loader />
      </Card>
    );
  }

  return (
    <Card className="m-0 p-3 h-100">
      <CardHeader className="bg-white border-0 p-0 pb-2">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <div className="rounded-circle d-flex align-items-center justify-content-center p-2 bg-soft-blue">
              <FontAwesomeIcon icon={faCalendar} size="lg" />
            </div>
            <h4 className="mb-0 ml-3 font-weight-bold">Scheduling</h4>
          </div>
          <Button
            size="lg"
            color="link"
            className="p-0 text-dark"
            onClick={toggleModal}
          >
            <FontAwesomeIcon icon={faEdit} />
          </Button>
        </div>
      </CardHeader>
      <CardBody className="p-0">
        <DayPicker
          className="m-0 w-100"
          month={month}
          onMonthChange={setMonth}
          mode="single"
          showOutsideDays
          modifiers={modifiers}
          modifiersStyles={modifiersStyles}
          classNames={{
            months: "w-100",
            month: "w-100",
            table: "w-100",
            head_cell: "text-center",
            cell: "align-middle",
            day: "d-flex align-items-center justify-content-center mx-auto",
            today: "fw-bold text-primary",
          }}
          styles={{
            day: {
              width: "2rem",
              height: "2rem",
              cursor: "default",
              backgroundColor: "transparent",
              "--rdp-background-color_hover": "transparent",
            },
            root: {
              backgroundColor: "#f7f7f7",
              padding: "0",
              borderRadius: "0.25rem",
              "--rdp-background-color_hover": "transparent",
            },
          }}
          components={{
            Caption: () => (
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                  <span
                    className="rdp-nav_button"
                    onClick={() =>
                      setMonth(moment(month).subtract(1, "year").toDate())
                    }
                  >
                    {"<<"}
                  </span>
                  <span
                    className="rdp-nav_button"
                    onClick={() =>
                      setMonth(moment(month).subtract(1, "month").toDate())
                    }
                  >
                    {"<"}
                  </span>
                </div>
                <div className="h5 mb-0">
                  {moment(month).format("MMM YYYY")}
                </div>
                <div>
                  <span
                    className="rdp-nav_button"
                    onClick={() =>
                      setMonth(moment(month).add(1, "month").toDate())
                    }
                  >
                    {">"}
                  </span>
                  <span
                    className="rdp-nav_button"
                    onClick={() =>
                      setMonth(moment(month).add(1, "year").toDate())
                    }
                  >
                    {">>"}
                  </span>
                </div>
              </div>
            ),
          }}
        />

        <Row className="mt-2">
          {legendItems.map((item, index) => (
            <Col md={6} key={index} className="d-flex align-items-center mt-1">
              <span
                className="d-inline-block mr-2 rounded-circle"
                style={{
                  width: "15px",
                  height: "15px",
                  border: `2px solid ${item.color}`,
                  backgroundColor: `${item.color}20`,
                }}
              />
              <span className="d-inline-block">{item.label}</span>
            </Col>
          ))}
        </Row>
      </CardBody>
      {isModalOpen && (
        <SchedulingModal
          onClose={toggleModal}
          onConfirm={handleSaveDates}
          dates={modalDates}
        />
      )}
      {isConfirmationModalOpen && (
        <ConfirmationModal
          title="Confirmation"
          body="Are you sure you want to save these dates?"
          onSubmit={doSave}
          onClose={() => setIsConfirmationModalOpen(false)}
        />
      )}
      {isInformationModalOpen && (
        <InformationModal
          title={informationModalData.title}
          body={informationModalData.body}
          onClose={() => setIsInformationModalOpen(false)}
        />
      )}
    </Card>
  );
};

export default Scheduling;
